// helpers/normalise.js  (new small util)
import { stringToBoolean } from "../util/helper";
import * as AD from "../constants/attributeDetails";

const ALL_FIELD_DEFS = [
  AD.molecularScaffold, AD.molecularScaffoldAdvance,
  AD.learningRelated,  AD.learningRelatedAdvance,
  AD.contraintsAdvance,
  AD.constraints1, AD.constraints2, AD.constraints3, AD.constraints4,
  AD.constraints9,
  AD.w_filters_1, AD.w_filters_2, AD.w_filters_3, AD.w_filters_4,
  AD.w_filters_5, AD.w_filters_6, AD.w_filters_7,
  AD.w_filters_8, AD.w_filters_9, AD.w_filters_10, AD.w_filters_11,
].flat();

const W_FILTER_FIELD_MAP = [
  { filterKeys: ["w_amw"], lowKey: "w_amw_0", highKey: "w_amw_1" },
  { filterKeys: ["w_nchiral"], toggleKey: "limit_chiral_centers", lowKey: "w_nchiral_0", highKey: "w_nchiral_1" },
  { filterKeys: ["w_lrsz"], toggleKey: "limit_ring_size", lowKey: "w_lrsz_0", highKey: "w_lrsz_1" },
  {
    filterKeys: ["w_arring", "w_arrings"],
    toggleKey: "limit_number_of_aromatic_rings",
    lowKey: "w_arrings_0",
    highKey: "w_arrings_1",
  },
  { filterKeys: ["w_nrings"], toggleKey: "limit_number_of_rings", lowKey: "w_nrings_0", highKey: "w_nrings_1" },
  { filterKeys: ["w_rotbond"], toggleKey: "limit_rotatable_bonds", lowKey: "w_rotbond_0", highKey: "w_rotbond_1" },
  { filterKeys: ["w_halogen"], toggleKey: "limit_halogens", lowKey: "w_halogen_0", highKey: "w_halogen_1" },
  { filterKeys: ["w_ro5_ohnh"], toggleKey: "Limit_OH_NH", lowKey: "w_ro5_ohnh_0", highKey: "w_ro5_ohnh_1" },
  {
    filterKeys: ["w_nvrtspsa"],
    toggleKey: "limit_polar_surface_area",
    lowKey: "w_nvrtspsa_0",
    highKey: "w_nvrtspsa_1",
  },
  { filterKeys: ["w_hdons"], toggleKey: "limit_H_bond_donors", lowKey: "w_hdons_0", highKey: "w_hdons_1" },
  { filterKeys: ["w_hacts"], toggleKey: "limit_H_bond_acceptors", lowKey: "w_hacts_0", highKey: "w_hacts_1" },
];

const toNumberIfNumeric = (value) => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return value;

  const trimmed = value.trim();
  if (!trimmed) return value;

  const numericValue = Number(trimmed);
  return Number.isFinite(numericValue) ? numericValue : value;
};

const parseRange = (value) => {
  if (value === null || value === undefined || value === "") return null;

  if (Array.isArray(value)) {
    if (value.length < 2) return null;
    return [toNumberIfNumeric(value[0]), toNumberIfNumeric(value[1])];
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (parsed !== value) return parseRange(parsed);
    } catch {
      const parts = value.split(",").map((part) => part.trim());
      if (parts.length >= 2) return [toNumberIfNumeric(parts[0]), toNumberIfNumeric(parts[1])];
    }
  }

  if (typeof value === "object") {
    const low = value.min ?? value.low ?? value.lower ?? value[0];
    const high = value.max ?? value.high ?? value.upper ?? value[1];
    if (low !== undefined && high !== undefined) {
      return [toNumberIfNumeric(low), toNumberIfNumeric(high)];
    }
  }

  return null;
};

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

const expandWFilters = (casted, wFilters) => {
  if (!wFilters || typeof wFilters !== "object") return;

  W_FILTER_FIELD_MAP.forEach(({ filterKeys, toggleKey, lowKey, highKey }) => {
    const sourceKey = filterKeys.find((key) => hasOwn(wFilters, key));
    if (!sourceKey) return;

    const range = parseRange(wFilters[sourceKey]);
    if (!range) {
      if (toggleKey) casted[toggleKey] = false;
      return;
    }

    casted[lowKey] = range[0];
    casted[highKey] = range[1];
    if (toggleKey) casted[toggleKey] = true;
  });
};

export function normaliseFromBackend(raw = {}) {
  const casted = { ...raw };

  ALL_FIELD_DEFS.forEach(f => {
    const key = f.label;
    if (!(key in raw)) return;

    const val = raw[key];

    switch (f.type) {
      case "toggle":
        casted[key] = typeof val === "boolean" ? val : stringToBoolean(val);
        break;

      case "number":
        casted[key] = typeof val === "number" ? val : Number(val);
        break;

      case "slider":
        // slider’s two ends are kept separately in backend; leave as‑is
        break;

      case "select":
        // most selects use numeric options except ["cpu","cuda"] etc.
        // Cast numeric-looking strings to Number, leave others.
        if (/^\d+(\.\d+)?$/.test(val)) casted[key] = Number(val);
        break;

      default:
        // text / file names stay strings
        break;
    }
  });

  expandWFilters(casted, raw.w_filters);

  return casted;
}
